import { ApiError } from '@/teoCore/errors/ApiError';
import { TeoError } from '@/teoCore/errors/TeoError';
import type {
  AiSuggestion,
  AiSuggestionV2Request,
  APITeoMarker,
  MarkerColor,
  MarkerGroupName,
  WordListCategory,
  WordListData,
  WordListEntry,
  WordListScanRequest,
  WordListScanResult,
} from '@/teoCore/TeoApi';
import { arraysEqual } from '@/teoCore/utility/miscFunctions';
import { TeoEventTarget, type TeoEventMap } from '@/teoCore/utility/TeoEventTarget';

import type { TeoText } from '../TeoText';
import type { TeoView } from '../TextUi';
import type { MarkerDetails, UiWordListEntry, WordListHandler } from './WordListHandler';

export type WordListSubHandlerEvents = {
  currentEntriesChanged: (detail: UiWordListEntry[]) => void;
  entryFocused: (detail: UiWordListEntry | undefined) => void;
  scanPending: (detail: boolean) => void;
  gotScanResult: (detail: WordListScanResult) => void;
};

const wordListColors: Record<string, MarkerColor> = {
  'corporate-text-pruefung': 'warning',
  'corporate-schreibweisen': 'error',
  'corporate-text-rules': 'accent1',
};

export abstract class WordListSubHandler {
  lists: WordListData[] = [];
  private currentListIds: number[] = [];
  private _currentEntries: UiWordListEntry[] = [];
  private _focusedEntry: UiWordListEntry | undefined;
  private _focusedMarker: MarkerDetails | undefined;
  protected relationOffset: number = 0;
  public lastScanTime: number = 0;
  public markers: APITeoMarker[] = [];
  /** Shown under the editor loading animation while this handler scans. */
  protected loadingMessage: string = '';
  constructor(
    protected wordLists: WordListHandler,
    protected text: TeoText,
    public events: TeoEventTarget<WordListSubHandlerEvents & TeoEventMap>,
    protected viewName: TeoView,
    protected uiWLName: keyof typeof text.ui.state,
    protected uiCategoryName: keyof typeof text.ui.state,
  ) {
    this.wordLists.events.addEventListener('entryMarkerClicked', (markerDetails) => {
      const entry = this.getEntryByRelation(markerDetails.markerId);
      if (entry) this.focusEntry(entry, markerDetails);
    });
    this.text.store.addEventListener('view', (viewName: TeoView) => {
      if (viewName === this.viewName) {
        this.selectLists([]);
        this.wordLists.mark(this.currentLists);
      }
    });
    this.text.events.addEventListener('editable', (editable: boolean) => {
      if (!editable) this.unFocusEntry();
    });
    this.text.ui.events.addEventListener(this.uiWLName, () => {
      this.syncSelectedLists();
    });
    this.text.ui.events.addEventListener(this.uiCategoryName, () => {
      console.log('Category changed:', this.text.ui.state[this.uiCategoryName]);
      this.syncSelectedLists();
    });
  }

  get isUpToDate(): boolean {
    return this.lastScanTime > 0 && this.text.lastEdited < this.lastScanTime;
  }

  get currentEntries(): UiWordListEntry[] {
    return this._currentEntries;
  }

  set currentEntries(entries: UiWordListEntry[]) {
    this._currentEntries = entries;
    this.events.triggerEvent('currentEntriesChanged', entries);
  }

  get currentLists(): WordListData[] {
    if (!this.currentListIds.length) return [...this.lists];
    return this.lists.filter((list) => this.currentListIds.includes(list.id));
  }

  get focusedEntry(): UiWordListEntry | undefined {
    return this._focusedEntry;
  }

  get focusedMarker(): MarkerDetails | undefined {
    return this._focusedMarker;
  }

  getEntryByRelation(relation: string): UiWordListEntry | undefined {
    return this._currentEntries.find((entry) => entry.key === relation);
  }

  clickEntry(entry: UiWordListEntry): void {
    this.text.markers.triggerMarkerClick(entry.key);
  }

  unFocusEntry(): void {
    this.focusEntry(undefined);
  }

  private focusEntry(entry?: UiWordListEntry, markerDetails?: MarkerDetails) {
    if (this._focusedEntry === entry) return;
    this._focusedEntry = entry;
    this._focusedMarker = markerDetails;
    this.text.markers.focusMarkers(entry ? [entry.key] : []);
    this.events.triggerEvent('entryFocused', entry);
  }

  useSuggestion(suggestionKey: string): void {
    if (!this._focusedEntry) {
      console.error('No focused entry to apply suggestion.');
      return;
    }
    const suggestion = this._focusedEntry.suggestions.find((s) => s.key === suggestionKey);
    if (!suggestion) {
      console.error('No suggestion found for key:', suggestionKey);
      return;
    }
    if (suggestion.useAi) {
      this.addAiSuggestion([suggestion.suggestion]);
    } else {
      this.wordLists.replaceInText(this._focusedEntry.key, suggestion);
      this._focusedEntry.isChecked.set(true);
    }
  }

  selectLists(ids: number[]): void {
    if (arraysEqual(ids, this.currentListIds)) return;
    this.currentListIds = ids;
    this.syncCurrentEntries();
  }

  getFoundEntries(wordList: WordListData): WordListEntry[] {
    return this.wordLists.getWordListFoundEntries(wordList);
  }

  async addAiSuggestion(replacements?: string[]): Promise<void> {
    if (!this._focusedMarker || !this._focusedEntry) {
      console.error('Missing marker or WordListEntry to generate AI suggestion.');
      return;
    }
    const markerContext = this.text.markers.createMarkerContext(
      this._focusedMarker.markerId,
      'sentence',
    );
    if (!markerContext || !this.text.core.dynamicConfig.options.evaluationProfileId) {
      console.error('Missing marker context or evaluation profile.');
      return;
    }

    if (!this.text.state.currentTemplateId) {
      console.error('Missing selected prompt options template.');
      return;
    }

    if (!this.text.state.allPromptOptionsSelected) {
      console.error('Missing selected prompt options.');
      return;
    }

    const currentEntry = this._focusedEntry;
    currentEntry.aiLoading.set(true);

    if (!replacements) {
      replacements = this._focusedEntry.suggestions.map((sug) => sug.suggestion);
    }
    const ruleText = this._focusedEntry.aiFixText ?? this._focusedEntry.infoText;

    const instructionParts = ruleText ? [`###Regel:\n${ruleText}`] : [];

    if (replacements && replacements.length > 1) {
      instructionParts.push(
        `###Synonyme:\n${replacements.join('\n')}`,
        'Ersetze das Wort oder die Formulierung durch eines der Synonyme.',
      );
    } else if (replacements?.length === 1) {
      instructionParts.push(
        `###Synonym:\n${replacements[0]}`,
        'Ersetze das Wort oder die Formulierung durch das Synonym.',
      );
    } else {
      instructionParts.push('Behebe den Verstoß gegen die Regel.');
    }

    const instruction = instructionParts.join('\n\n');

    const req2: AiSuggestionV2Request = {
      context: markerContext,
      evaluation_profile_id: this.text.core.dynamicConfig.options.evaluationProfileId,
      template_id: this.text.state.currentTemplateId,
      selected_options: this.text.state.selectedPromptOptions as Record<string, string>,
      use_whitelist: this.text.whiteListActive,
      instruction,
    };

    try {
      const response = await this.text.core.api.getAiSuggestionV2(req2);
      if (TeoError.isError(response)) {
        throw response;
      }
      // A later scan may have replaced this finding with a different item that
      // happens to reuse the same marker relation / UI key.
      if (!this.currentEntries.includes(currentEntry)) {
        return;
      }
      const newAiSuggestions: AiSuggestion[] =
        response.suggestions?.map((suggestion) => ({
          context: markerContext,
          text: suggestion.text,
          teo_result: suggestion.result,
          hidden: suggestion.result.tpi.percentage < 0,
        })) ?? [];
      currentEntry.aiSuggestions = [...(currentEntry.aiSuggestions || []), ...newAiSuggestions];
      this.currentEntries = [...this.currentEntries];
      console.log('AI suggestion added to entry:', currentEntry);
    } catch (error) {
      console.error('Failed to get AI suggestion:', error);
      this.text.core.notifications.addError(error);
    } finally {
      currentEntry.aiLoading.set(false);
    }
  }

  protected syncSelectedLists(): void {
    const selectedWordList = this.text.ui.state[this.uiWLName];
    const selectedCategory = this.text.ui.state[this.uiCategoryName];
    if (selectedWordList) {
      const list = this.lists.find((l) => l.name === selectedWordList);
      if (list) {
        this.selectLists([list.id]);
        this.unFocusEntry();
      } else {
        console.warn('Word list not found:', selectedWordList);
        this.selectLists([]);
      }
    } else if (selectedCategory) {
      let lists = [];
      // if (selectedCategory === 'checks') {
      // lists = this.lists.filter((l) => l.relation_type === 5);
      // } else {
      lists = this.lists;
      // }
      if (lists.length > 0) {
        this.selectLists(lists.map((l) => l.id));
        this.unFocusEntry();
      } else {
        console.warn('No word lists found for category:', selectedCategory);
        this.selectLists([]);
      }
    } else {
      this.selectLists([]);
      this.unFocusEntry();
    }
  }

  protected createUiWordListEntry(entry: WordListEntry, wordList: WordListData): UiWordListEntry {
    return this.wordLists.createUiWordListEntry(entry, wordList, this.relationOffset);
  }

  protected syncCurrentEntries() {
    if (!this.lists) {
      console.error('No word lists available.');
      this.currentEntries = [];
      return;
    }
    const currentEntriesByKey = new Map(this.currentEntries.map((entry) => [entry.key, entry]));
    const entries: UiWordListEntry[] = [];
    this.lists.forEach((wordList) => {
      if (this.currentListIds.includes(wordList.id) || !this.currentListIds.length) {
        entries.push(
          ...this.getFoundEntries(wordList).map((entry) => {
            const nextEntry = this.createUiWordListEntry(entry, wordList);
            const currentEntry = currentEntriesByKey.get(nextEntry.key);
            // Keys are marker relations, which are reused across scans. Only keep
            // reactive UI state when this is still the same finding.
            if (!currentEntry || !this.isSameFinding(currentEntry, nextEntry)) {
              return nextEntry;
            }

            const { aiLoading, aiSuggestions, isChecked } = currentEntry;
            Object.assign(currentEntry, nextEntry, { aiLoading, aiSuggestions, isChecked });
            return currentEntry;
          }),
        );
      }
    });
    this.currentEntries = entries;
    if (this._focusedEntry && !entries.includes(this._focusedEntry)) {
      this.unFocusEntry();
    }
    if (this.text.store.state.view === this.viewName) {
      this.wordLists.mark(this.currentLists);
    }
  }

  private isSameFinding(currentEntry: UiWordListEntry, nextEntry: UiWordListEntry): boolean {
    return (
      currentEntry.entryId === nextEntry.entryId &&
      currentEntry.variantLabel === nextEntry.variantLabel
    );
  }

  private getMarkerColor(markerName: string): MarkerColor {
    for (const [key, color] of Object.entries(wordListColors)) {
      if (markerName.startsWith(key)) {
        return color;
      }
    }
    return 'neutral';
  }

  async scan(wordListCategory: WordListCategory) {
    return this.text.store.withLoading(() => this.runScan(wordListCategory), this.loadingMessage);
  }

  private async runScan(wordListCategory: WordListCategory) {
    const textArray = this.text.getTextArray?.();
    if (!textArray) {
      console.error('No text array available for scanning.');
      return;
    }
    this.events.triggerEvent('scanPending', true);
    const request: WordListScanRequest = {
      text: textArray,
      ignored_entries: this.wordLists.ignoredEntries,
      word_list_category: wordListCategory,
      selected_options: this.text.state.selectedPromptOptions as Record<string, string>,
    };

    if (this.text.core.dynamicConfig.options.useExpParser) {
      request.use_exp_parser = true;
    }

    const response = await this.text.core.api.getWordListScan(request);
    if (ApiError.matches(response)) {
      console.error('Error in word list scan:', response);
      this.events.triggerEvent('scanPending', false);
      this.text.core.notifications.addError(response.error);
      return;
    }
    // Marker relations are reused across scans, so drop previous UI entries
    // before applying the new result. Otherwise AI suggestions / checked state
    // from the last scan can attach to a different finding with the same key.
    this.unFocusEntry();
    this.currentEntries = [];
    this.markers = [];
    console.log('Word list scan result:', response);
    response.markers.forEach((marker) => {
      this.markers.push({
        ...marker,
        relation:
          marker.relation === null ?
            null
          : marker.relation.toString() + this.relationOffset.toString(),
        groupName: wordListCategory as MarkerGroupName,
        color: marker.color || this.getMarkerColor(marker.marker),
      });
    });
    this.lastScanTime = Date.now();
    this.events.triggerEvent('gotScanResult', response);
    this.events.triggerEvent('scanPending', false);
    this.text.events.triggerEvent('analysisStateChanged', void 0);
  }
}
